import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  Alert,
  TextField,
  Paper,
  CircularProgress,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import ConfirmModal from "../reuseable-components/ConfirmModal";
import {
  uploadPhotos,
  getGalleryCounts,
  getAllGalleryPhotos,
  deleteGalleryPhoto,
  getGalleryCategories,
  createGalleryCategory,
  updateGalleryCategory,
  deleteGalleryCategory,
} from "../../../apis/admin-apis/dashboard-apis/PhotoApis";

const PhotosTab = ({ refreshDashboard }) => {
  const [eventCategory, setEventCategory] = useState("");
  const [caption, setCaption] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [groupedPhotos, setGroupedPhotos] = useState({});
  const [galleryCounts, setGalleryCounts] = useState({});
  const [uploadError, setUploadError] = useState("");
  const [categories, setCategories] = useState([]);

  // Category management state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryError, setCategoryError] = useState("");
  const [categoryLoading, setCategoryLoading] = useState(false);

  const [confirmState, setConfirmState] = useState({
    open: false,
    photoId: null,
  });

  const [categoryConfirm, setCategoryConfirm] = useState({
    open: false,
    categoryId: null,
    categoryName: "",
  });

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getGalleryCategories();
      if (res.success) {
        setCategories(res.categories || []);
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  }, []);

  const fetchGalleryData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [countsRes, photosRes] = await Promise.all([
        getGalleryCounts(),
        getAllGalleryPhotos(),
      ]);

      if (countsRes.success) {
        setGalleryCounts(countsRes.counts || {});
      }

      if (photosRes.success) {
        setGroupedPhotos(photosRes.grouped || {});
      }
    } catch (error) {
      console.error("Failed to fetch gallery data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchGalleryData();
  }, [fetchCategories, fetchGalleryData]);

  // Compress image to stay under Netlify's ~1MB request body limit
  const compressImage = (file) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith("image/")) {
        resolve(file);
        return;
      }

      // Already under 800KB, safe to upload as-is
      if (file.size < 800 * 1024) {
        resolve(file);
        return;
      }

      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);

        const canvas = document.createElement("canvas");
        let { width, height } = img;
        const maxWidth = 1200;

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Try quality 0.7 first, then lower if still too big
        const tryCompress = (quality) => {
          canvas.toBlob(
            (blob) => {
              if (blob.size > 800 * 1024 && quality > 0.3) {
                tryCompress(quality - 0.15);
              } else {
                const compressed = new File([blob], file.name, {
                  type: "image/jpeg",
                  lastModified: file.lastModified,
                });
                resolve(compressed);
              }
            },
            "image/jpeg",
            quality,
          );
        };

        tryCompress(0.7);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file);
      };

      img.src = url;
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFiles(Array.from(e.target.files));
      setUploadError("");
    }
  };

  const handleUpload = async () => {
    if (!eventCategory || selectedFiles.length === 0) {
      setUploadError("Please select a category and at least one photo file.");
      return;
    }

    setIsUploading(true);
    setUploadError("");
    const total = selectedFiles.length;
    let uploaded = 0;
    const failed = [];

    try {
      for (const file of selectedFiles) {
        try {
          const compressed = await compressImage(file);
          const formData = new FormData();
          formData.append("photos", compressed);
          formData.append("eventName", eventCategory);
          formData.append("caption", caption);
          await uploadPhotos(formData);
          uploaded++;
        } catch (err) {
          failed.push(file.name);
        }
      }

      if (uploaded > 0) {
        setEventCategory("");
        setCaption("");
        setSelectedFiles([]);
        const fileInput = document.getElementById("photo-upload-input");
        if (fileInput) fileInput.value = "";
        fetchGalleryData();
        if (refreshDashboard) refreshDashboard();
      }

      if (failed.length > 0) {
        alert(`${uploaded}/${total} uploaded. Failed: ${failed.join(", ")}`);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      alert(error.message || "Failed to upload photos.");
    } finally {
      setIsUploading(false);
    }
  };

  // ── Category CRUD handlers ──

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      setCategoryError("Category name is required.");
      return;
    }
    setCategoryLoading(true);
    setCategoryError("");
    try {
      const res = editingCategory
        ? await updateGalleryCategory(editingCategory._id, newCategoryName.trim())
        : await createGalleryCategory(newCategoryName.trim());

      if (res.success) {
        setNewCategoryName("");
        setEditingCategory(null);
        setCategoryDialogOpen(false);
        fetchCategories();
        fetchGalleryData();
      }
    } catch (error) {
      setCategoryError(error.error || error.message || "Failed to save category.");
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setNewCategoryName(cat.name);
    setCategoryError("");
    setCategoryDialogOpen(true);
  };

  const handleDeleteCategoryConfirm = async () => {
    setCategoryLoading(true);
    try {
      const res = await deleteGalleryCategory(categoryConfirm.categoryId);
      if (res.success) {
        fetchCategories();
        fetchGalleryData();
      }
    } catch (error) {
      alert(error.error || error.message || "Failed to delete category.");
    } finally {
      setCategoryLoading(false);
      setCategoryConfirm({ open: false, categoryId: null, categoryName: "" });
    }
  };

  // ── Photo delete handlers ──

  const handleOpenDeleteConfirm = (photoId) => {
    setConfirmState({ open: true, photoId });
  };

  const handleCloseConfirm = () => {
    setConfirmState({ open: false, photoId: null });
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await deleteGalleryPhoto(confirmState.photoId);
      if (response.success) {
        fetchGalleryData();
        if (refreshDashboard) refreshDashboard();
      }
    } catch (error) {
      console.error("Failed to delete photo:", error);
      alert(error.message || "Failed to delete photo.");
    } finally {
      setIsDeleting(false);
      handleCloseConfirm();
    }
  };

  const formatPhotoDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
  };

  return (
    <Box
      className="photos-tab-container"
      sx={{ p: { xs: 1, md: 3 }, bgcolor: "#fff" }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CameraAltIcon sx={{ fontSize: "20px" }} />
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#0F1E3D", fontSize: "16px" }}
          >
            Photo Gallery Management
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            fetchCategories();
            fetchGalleryData();
          }}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <RefreshIcon />
            )
          }
          sx={{
            bgcolor: "#D4A017",
            "&:hover": { bgcolor: "#B8860B" },
            textTransform: "none",
          }}
        >
          {isLoading ? "Refreshing..." : "Refresh"}
        </Button>
      </Box>

      {uploadError && (
        <Alert
          severity="error"
          sx={{ mb: 1.5, py: 0.5, "& .MuiAlert-message": { fontSize: "12px" } }}
        >
          {uploadError}
        </Alert>
      )}

      <Box sx={{ borderBottom: "1px solid #EAE2D1", mb: 3 }} />

      {/* ── Category Management Section ── */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          p: 2.5,
          mb: 3,
          bgcolor: "#FAFAFA",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
            Gallery Categories
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingCategory(null);
              setNewCategoryName("");
              setCategoryError("");
              setCategoryDialogOpen(true);
            }}
            sx={{
              bgcolor: "#0F1E3D",
              "&:hover": { bgcolor: "#1a2e5a" },
              textTransform: "none",
              fontSize: "12px",
            }}
          >
            Add Category
          </Button>
        </Box>

        {categories.length === 0 ? (
          <Typography sx={{ color: "#888", fontSize: "13px" }}>
            No categories yet. Add one to start uploading photos.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {categories.map((cat) => (
              <Chip
                key={cat._id}
                icon={<FolderIcon sx={{ fontSize: "16px !important" }} />}
                label={`${cat.name} (${galleryCounts[cat.name] || 0})`}
                onDelete={() =>
                  setCategoryConfirm({
                    open: true,
                    categoryId: cat._id,
                    categoryName: cat.name,
                  })
                }
                deleteIcon={<DeleteIcon sx={{ fontSize: "16px !important" }} />}
                onClick={() => handleEditCategory(cat)}
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #D4A017",
                  fontWeight: 600,
                  fontSize: "12px",
                  "& .MuiChip-deleteIcon": { color: "#D32F2F" },
                  cursor: "pointer",
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ── Upload Section ── */}
      <Box
        className="upload-section"
        sx={{
          border: "1px dashed #D4A017",
          borderRadius: "8px",
          p: 3,
          bgcolor: "#FDFCF9",
          mb: 4,
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 2, fontSize: "14px" }}>
          Upload Photos
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: "300px" }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Category *
            </Typography>
            <Select
              fullWidth
              size="small"
              value={eventCategory}
              onChange={(e) => {
                setEventCategory(e.target.value);
                setUploadError("");
              }}
              displayEmpty
              sx={{ bgcolor: "#fff", mt: 0.5 }}
            >
              <MenuItem value="">-- Select Category --</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat._id} value={cat.name}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ flex: 1, minWidth: "300px" }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Photo Files * (select multiple)
            </Typography>
            <Box
              sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}
            >
              <input
                id="photo-upload-input"
                type="file"
                onChange={handleFileChange}
                accept="image/*"
                multiple
                style={{ fontSize: "12px" }}
              />
            </Box>
            {selectedFiles.length > 1 && (
              <Typography
                variant="caption"
                sx={{ color: "#666", mt: 0.5, display: "block" }}
              >
                {selectedFiles.length} files selected
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Caption (Optional — applied to all)
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="e.g., Meeting with local small business owners"
            sx={{ bgcolor: "#fff", mt: 0.5 }}
          />
        </Box>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={isUploading}
          startIcon={
            isUploading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <FileUploadIcon />
            )
          }
          sx={{
            bgcolor: "#D4A017",
            "&:hover": { bgcolor: "#B8860B" },
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          {isUploading
            ? `Uploading ${selectedFiles.length} photo(s)...`
            : `Upload ${selectedFiles.length > 0 ? selectedFiles.length + " " : ""}Photo(s)`}
        </Button>
      </Box>

      {/* ── Stats Grid ── */}
      {categories.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 4 }}>
          {categories.map((cat) => (
            <Box
              key={cat._id}
              sx={{
                flex: "1 1 calc(33.333% - 12px)",
                minWidth: "200px",
                border: "1px solid #eee",
                borderRadius: "8px",
                p: 2,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <FolderIcon sx={{ color: "#D4A017" }} />
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
                {galleryCounts[cat.name] || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: "#666" }}>
                {cat.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* ── Gallery Sections ── */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : Object.keys(groupedPhotos).length === 0 ? (
        <Typography sx={{ textAlign: "center", py: 5, color: "#888" }}>
          No photos found in the gallery.
        </Typography>
      ) : (
        Object.keys(groupedPhotos).map((category, sIdx) => (
          <Box key={sIdx} sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <FolderIcon sx={{ fontSize: "18px", color: "#D4A017" }} />
              <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
                {category} ({groupedPhotos[category].length})
              </Typography>
            </Box>
            <Box sx={{ borderBottom: "1px solid #D4A017", mb: 2 }} />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {groupedPhotos[category].map((photo) => (
                <Box key={photo._id} sx={{ width: "160px" }}>
                  <Paper variant="outlined" sx={{ overflow: "hidden", mb: 1 }}>
                    <Box
                      component="img"
                      src={photo.url}
                      sx={{
                        width: "100%",
                        height: "120px",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Paper>
                  <Typography noWrap sx={{ fontSize: "11px", fontWeight: 600 }}>
                    {photo.filename || "Untitled"}
                  </Typography>
                  <Typography sx={{ fontSize: "10px", color: "#888", mb: 1 }}>
                    {formatPhotoDate(photo.uploadedAt || photo.createdAt)}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      onClick={() => window.open(photo.url, "_blank")}
                      startIcon={
                        <VisibilityIcon sx={{ fontSize: "12px !important" }} />
                      }
                      sx={{ bgcolor: "#0F1E3D", fontSize: "10px", py: 0.5 }}
                    >
                      View
                    </Button>
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      onClick={() => handleOpenDeleteConfirm(photo._id)}
                      startIcon={
                        <DeleteIcon sx={{ fontSize: "12px !important" }} />
                      }
                      sx={{ bgcolor: "#D32F2F", fontSize: "10px", py: 0.5 }}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Box>
          </Box>
        ))
      )}

      {/* ── Category Create/Edit Dialog ── */}
      <Dialog
        open={categoryDialogOpen}
        onClose={() => !categoryLoading && setCategoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "8px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            bgcolor: "#081B44",
            color: "#fff",
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "18px" }}>
            {editingCategory ? "Edit Category" : "Add Category"}
          </Typography>
          <IconButton
            onClick={() => setCategoryDialogOpen(false)}
            disabled={categoryLoading}
            sx={{
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.35)",
              width: 30,
              height: 30,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {categoryError && (
            <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
              {categoryError}
            </Alert>
          )}
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Category Name *
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={newCategoryName}
            onChange={(e) => {
              setNewCategoryName(e.target.value);
              setCategoryError("");
            }}
            placeholder="e.g., Peach Festival"
            sx={{ mt: 0.5, mb: 3 }}
            onKeyDown={(e) => e.key === "Enter" && handleCreateCategory()}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              pt: 2,
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <Button
              onClick={() => setCategoryDialogOpen(false)}
              disabled={categoryLoading}
              sx={{
                bgcolor: "#E5E7EB",
                color: "#0F1E3D",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateCategory}
              disabled={categoryLoading}
              sx={{
                bgcolor: "#D4A017",
                "&:hover": { bgcolor: "#B8860B" },
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              {categoryLoading
                ? "Saving..."
                : editingCategory
                  ? "Update"
                  : "Create"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Photo Delete Confirm ── */}
      <ConfirmModal
        open={confirmState.open}
        handleClose={handleCloseConfirm}
        onConfirm={handleConfirmDelete}
        title="Delete Photo"
        message="Are you sure you want to delete this photo?"
        confirmLabel="Delete"
        loading={isDeleting}
      />

      {/* ── Category Delete Confirm ── */}
      <ConfirmModal
        open={categoryConfirm.open}
        handleClose={() =>
          !categoryLoading &&
          setCategoryConfirm({ open: false, categoryId: null, categoryName: "" })
        }
        onConfirm={handleDeleteCategoryConfirm}
        title="Delete Category"
        message={`Are you sure you want to delete "${categoryConfirm.categoryName}"? This will only work if the category has no photos.`}
        confirmLabel="Delete"
        loading={categoryLoading}
      />
    </Box>
  );
};

export default PhotosTab;
